// @ts-strict-ignore
/**
 * A query-builder "block" wires together the following query components:
 * - header (e.g. "USERS THAT <DID>")
 * - search-bar input (e.g. "Select event")
 * - dropdown (e.g. event selector, time picker)
 */
import isEqual from 'lodash/isEqual';
import React, {useState, useRef, useMemo, useEffect, useImperativeHandle} from 'react';

import {emitTrackEvent, rafRetry} from 'common/util/dom';
import {ButtonSize, ButtonTheme as ButtonThemes} from 'common/components/button/constants';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {AUTOMATED_CUSTOM_PROPERTY_TRACKING} from 'common/constants';
import {cohortsStore} from 'common/report/stores/cohorts';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {
  eventPropertyDefinitionsStore,
  groupPropertyDefinitionsStore,
  peoplePropertyDefinitionsStore,
} from 'common/report/stores/property-definitions';
import {DisplayContext as FilterMenuDisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {EntityTypeNameSingular} from 'common/report/util/saved-entities/types';
import {SelectAllMechanism} from 'common/components/items-menu/types';
import {Directions} from 'common/components/drop-menu/types';
import {PositioningStrategy} from 'common/components/tooltip/types';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SvgIconType} from 'common/components/svg-icon';
import {ContextPanelReact, ContextPanelReactProps} from 'common/widgets/context-panel';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useDomEvent} from 'common/hooks/useDomEvent';
import {useEventsController} from 'common/contexts/react/events-controller-context';
import {useTranslation} from 'common/i18n/hooks';
import {usePrevious} from 'common/hooks/usePrevious';

import {queryBuilderUtils} from '../query-builder/util';
import {SelectorType, TRACKING_EVENTS} from './constants';
import {BreakdownMenuReact} from './breakdown-menu';
import {EventsMenu, EventsMenuReact} from './events-menu';
import {FilterMenuReact} from './filter-menu';
import {MultiEventsMenuReact} from './multi-events-menu';
import {FormulasMenu, FormulasMenuReact} from './formulas-menu';
import {updateEntityDescription} from './utils/util';
import {MenuType, QueryBlockParams} from './types';
import {IncludeCustomPropertyType, PropertyCombineMode} from './properties-menu/types';
import {BehaviorsMenu, BehaviorsMenuReact} from './behaviors-menu';
import {ChildMenuInterface, ContextPanelRenderer} from './child-menu';
import {PropertiesMenuReact} from './properties-menu';
import {NewQueryBlock} from './new-query-block';

// Static definition of all registered child menus
const MENU_TYPE_MAP: Partial<Record<MenuType, any>> = {
  [MenuType.Behaviors]: BehaviorsMenu,
  [MenuType.Events]: EventsMenu,
  [MenuType.Formulas]: FormulasMenu,
};

const QueryBlock = React.forwardRef<HTMLDivElement, QueryBlockParams>((props, ref) => {
  const rootEl = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => rootEl.current);

  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const eventsController = useEventsController();
  const {print} = useTranslation();

  const [childMenu, setChildMenu] = useState<ChildMenuInterface>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(props.open);
  const [openedMenuForModal, setOpenedMenuForModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [keydownProxyEl, setKeydownProxyEl] = useState(null);

  const previousOpenProp = usePrevious(props.open);

  const onChildMenuUpdate = useChildMenuSync(childMenu);

  useEffect(() => {
    if (typeof eventsController.loading === `undefined`) {
      eventsController.fetchData();
    }
  }, [eventsController]);

  useDomEvent(rootEl.current, `track`, props.onTrack);
  useDomEvent(rootEl.current, `savedCustomEvent`, props.onSavedCustomEvent);

  const projectId = Number(props.projectId || getProjectId());
  const workspaceId = Number(props.workspaceId || getWorkspaceId());

  const openMenu = () => {
    setIsMenuOpen(true);
    props.onOpen?.();
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    childMenu?.handleCloseMenu?.();
    props.onClose?.();
  };

  // update internal open state if the external one changes
  useEffect(() => {
    if (
      typeof props.open !== `undefined` &&
      typeof previousOpenProp !== `undefined` &&
      props.open !== previousOpenProp
    ) {
      if (props.open) {
        openMenu();
      } else {
        closeMenu();
      }
    }
  });

  const selected = useMemo(() => {
    switch (props.type) {
      case MenuType.Events:
        return props.multiple ? new Set(props.selected || []) : props.selected;

      case MenuType.Filter:
        return (props.selected && new PropertyFilter(props.selected)) || props.selected;

      default:
        return props.selected;
    }
  }, [props.selected, props.type, props.multiple, props.displayContext]);

  const dispatchSelect = (detail) => {
    props.onSelect?.(detail);

    if (detail.commit ?? childMenu?.shouldCloseOnSelect?.()) {
      closeMenu();
    }
  };

  const track = (event, trackingProps, cancelable = true) => {
    if (!trackingProps.trackName) {
      return;
    }

    emitTrackEvent(rootEl.current, {
      eventName: event,
      props: {
        ...trackingProps,
        project_id: projectId,
        tab: trackingProps.trackName,
      },
      cancelable,
    });
  };

  const updateCohortDescription = (detail) => {
    const cohortStoreKey = detail.item.id;
    const descriptionBeforeUpdate = (cohortsStore.value[cohortStoreKey] as any).description;
    const cohortPropertyUpdate = {description: detail.description};

    const updateRequestObj = {
      projectId,
      workspaceId,
      cohortID: detail.item.id,
      cohort: cohortPropertyUpdate,
    };
    const undoRequestObj = {...updateRequestObj, cohort: {description: descriptionBeforeUpdate}};
    updateEntityDescription({
      entityStore: cohortsStore,
      newDescription: detail.description,
      storeKey: cohortStoreKey,
      updateRequestObj,
      undoRequestObj,
      entityType: `cohort property`,
      addToast,
      track,
    });
  };

  const updatePropertyDescription = (detail) => {
    const propertyStoreKey = detail.item.name;
    let propertyStore;

    if (detail.item.dataGroupId) {
      propertyStore = groupPropertyDefinitionsStore;
    } else {
      propertyStore =
        detail.item.type === EntityTypeNameSingular.EventProperty
          ? eventPropertyDefinitionsStore
          : peoplePropertyDefinitionsStore;
    }

    const descriptionBeforeUpdate = propertyStore.value[propertyStoreKey].description;

    let propertyDefinition;

    if (detail.item.id) {
      propertyDefinition = {id: detail.item.id, description: detail.description, dataGroupId: detail.item.dataGroupId};
    } else {
      propertyDefinition = {
        name: detail.item.name,
        resourceType: detail.item.type === EntityTypeNameSingular.EventProperty ? `Event` : `User`,
        description: detail.description,
        dataGroupId: detail.item.dataGroupId,
      };
    }

    const updateRequestObj = {
      projectId,
      workspaceId,
      propertyDefinition,
    };

    const undoRequestObj = {
      ...updateRequestObj,
      propertyDefinition: {...updateRequestObj.propertyDefinition, description: descriptionBeforeUpdate},
    };

    updateEntityDescription({
      entityStore: propertyStore,
      newDescription: detail.description,
      storeKey: propertyStoreKey,
      updateRequestObj,
      undoRequestObj,
      entityType: detail.item.type === EntityTypeNameSingular.EventProperty ? `event property` : `user property`,
      addToast,
      track,
    });
  };

  const updateCustomPropertyDescription = async (detail) => {
    const customPropertyId = detail.item.id;

    const customProperty = Object.assign(
      {},
      {...customPropertiesStore.value[customPropertyId], description: detail.description},
    );
    let extraProps = {};
    if (customProperty.pre_populated_entity_id) {
      extraProps = {
        ...extraProps,
        [AUTOMATED_CUSTOM_PROPERTY_TRACKING]: customProperty.pre_populated_entity_id,
      };
    }
    const oldDescription = customPropertiesStore.value[customPropertyId].description;
    const cancelable = false;
    track(
      TRACKING_EVENTS.EDIT_DESCRIPTION,
      {
        'New Description': detail.description,
        'old description': oldDescription,
        'access point': `context panel`,
        'Entity Type': `custom property`,
        ...extraProps,
      },
      cancelable,
    );
    try {
      await customPropertiesStore.put(customPropertyId, {
        projectId,
        workspaceId,
        customPropertyId,
        customProperty,
      });
      const undoCallback = async () => {
        await customPropertiesStore.put(customPropertyId, {
          projectId,
          workspaceId,
          customPropertyId,
          customProperty: {...customProperty, description: oldDescription},
        });
      };
      addToast({
        message: `'${detail.item.name}' has been updated`,
        type: ToastType.SUCCESS,
        undoable: true,
        undoCallback,
      });
    } catch (err) {
      addToast({message: `Failed to update the description`, type: ToastType.ERROR});
    }
  };

  const handleChildMenuOpenedModal = () => setIsModalOpen(true);

  const handleChildMenuClosedModal = () => {
    setIsModalOpen(false);
    if (openedMenuForModal) {
      closeMenu();
    }
  };

  // Handle actions from the context tooltip attached to the query block. First, give the
  // child menu a chance to handle the action. If it's not handled by child menu, then
  // handle the action directly here.
  const handleTooltipContextAction = (ev) => {
    const {handled, openedModal} = childMenu?.handleTooltipContextAction?.(ev) || {
      handled: false,
      openedModal: false,
    };
    if (!handled) {
      throw Error(`unhandled context panel action`);
    }
    setTooltipData(null);

    // If a modal that lives in the child menu is open, then the child menu has to be open.
    // This can happen if you click the edit action on the context panel attached to the query block.
    // This will open the child menu's modal. However, we also have to force open the child menu,
    // otherwise the modal would be hidden.
    if (openedModal) {
      openMenu();
      setOpenedMenuForModal(true);
      setIsModalOpen(true);
    }
  };

  const handleTooltipContextEditDescription = (ev) => {
    const detail = ev.detail;
    switch (detail.item.type) {
      case EntityTypeNameSingular.Cohort:
        updateCohortDescription(detail);
        break;
      case EntityTypeNameSingular.EventProperty:
        updatePropertyDescription(detail);
        break;
      case EntityTypeNameSingular.UserProperty:
        updatePropertyDescription(detail);
        break;
      case EntityTypeNameSingular.CustomProperty:
        updateCustomPropertyDescription(detail);
        break;
      default:
        break;
    }
  };

  const handleSelect = (ev) => dispatchSelect(ev.detail);

  // todo: move child menu autofocus to itself
  useEffect(() => {
    if (isMenuOpen && keydownProxyEl) {
      // This patch is equivalent fo throwing a sleep retry since
      // However, it is a goot fix while @pablo works on a real solution
      rafRetry(() => keydownProxyEl.focus());
    }
  }, [isMenuOpen]);

  const handleDelete = (ev?: Event) => {
    ev?.stopPropagation();
    props.onRemove?.();
  };

  const handleShowContextPanel = () => {
    const selectedHoverTooltipData = childMenu?.getSelectedHoverTooltipData?.();
    setTooltipData(selectedHoverTooltipData);

    if (selectedHoverTooltipData) {
      // Setting cancelable to false is needed to prevent mp-query-multi-block from swallowing the event
      const cancelable = false;
      track(
        TRACKING_EVENTS.HOVER_CONTEXT_PANEL,
        {
          'Entity name': selectedHoverTooltipData.header,
          'Query block type': props.type,
          'Has description': !!selectedHoverTooltipData.description,
          ...(childMenu?.getSelectedTrackingProps?.() || {}),
        },
        cancelable,
      );
    }
  };

  /**
   * A given resource context panel (event, cohort or properties) needs to be defined
   * in the context of that menu type, to prevent having resource specific logic
   * in this main component
   */
  const renderContextPanelFromMenu = (panelProps: ContextPanelReactProps) => {
    const contextPanelRenderer: ContextPanelRenderer = MENU_TYPE_MAP[props.type]?.contextPanelRenderer;

    return (
      contextPanelRenderer &&
      contextPanelRenderer({
        availableContexts: {
          metrics,
          behaviors,
        },
        tooltipData,
        handleSelect: dispatchSelect,
        handleDelete,
        track,
        additionalArgs: {
          'all-data-groups': props.allDataGroups,
          'project-has-people': props.projectHasPeople,
          'track-name': props.trackName,
        },
        contextPanelProps: {
          ...panelProps,
          ...(queryBuilderUtils.isCoreReport() ? {positionParent: props.contextPanelParentElement} : {}),
        },
      })
    );
  };

  const contextPanelProps: Partial<ContextPanelReactProps> = {
    positionStrategy: PositioningStrategy.Fixed,
    placement: queryBuilderUtils.isCoreReport() ? Directions.Right : Directions.Down,
    offset: queryBuilderUtils.isCoreReport() ? 8 : 4,
    showArrow: true,
    elref: `contextPanel`,
  };

  const selectedLabel = childMenu?.selectedLabel?.() || selected?.label;

  return (
    <NewQueryBlock
      ref={rootEl}
      label={(!props.inputOnly && selectedLabel) || props.label}
      autofocus={props.autofocus}
      browserTooltip={childMenu?.selectedBrowserTooltipLabel?.()}
      buttonSize={props.buttonSize}
      canOpenMenu={childMenu?.canOpenMenu?.()}
      className={`type-${props.type}`}
      contextPanelElement={
        renderContextPanelFromMenu(contextPanelProps) ?? (
          <ContextPanelReact
            {...contextPanelProps}
            data={tooltipData}
            positionParent={props.contextPanelParentElement}
            onTooltipContextAction={handleTooltipContextAction}
            onTooltipContextEditDescription={handleTooltipContextEditDescription}
          />
        )
      }
      tooltip={props.tooltip}
      disabled={props.disabled}
      dropMenuContainerClass={`for-${props.type}-menu`}
      elref={props.elref}
      header={props.headerText || props.headerSegments}
      helpText={props.helpText}
      inputOnly={props.inputOnly}
      isUnsaved={childMenu?.isUnsavedCustomEntity?.()}
      labelElement={props.labelRenderer?.()}
      labelHighlightSelections={props.highlightSelections && childMenu?.getSelections?.()}
      leftIcon={(props.selectorIcon ?? childMenu?.selectedLeftIcon?.()) as SvgIconType}
      rightIcon={(props.selectorRightIcon ?? childMenu?.selectedRightIcon?.()) as SvgIconType}
      menuAlignment={props.menuAlignment}
      menuOffset={props.menuOffset}
      open={isMenuOpen}
      onChangeOpen={(open) => (open ? openMenu() : closeMenu())}
      prefixLabel={props.prefixLabel ?? print(childMenu?.getPrefixLabel?.())}
      readonly={props.readonly}
      preventClose={props.preventClose}
      preventCloseSelectors={props.type === MenuType.Properties ? [`.mp-query-block-selector`] : []}
      removable={props.removable}
      selectorType={props.selectorType}
      theme={props.theme}
      tabIndex={props.tabIndex}
      onClick={props.onClick}
      onClickRemove={handleDelete}
      onLabelMouseEnter={() => childMenu?.handleLabelMouseEnter?.()}
      onLabelMouseLeave={() => childMenu?.handleLabelMouseLeave?.()}
      onSelectHeaderItem={props.onSelectHeaderItem}
      onShowContextPanel={handleShowContextPanel}
      menuElement={(() => {
        const open = isMenuOpen && !openedMenuForModal;
        const key = props.type;
        let otherOptions;

        if (props.otherGroupByOptions?.length) {
          otherOptions = props.otherGroupByOptions;
        } else if (props.otherFilterOptions?.length) {
          otherOptions = props.otherFilterOptions;
        } else if (props.otherAggregationOptions?.length) {
          otherOptions = props.otherAggregationOptions;
        }

        const setRef = (childMenuRef) => {
          if (childMenuRef && childMenuRef !== childMenu) {
            setChildMenu(childMenuRef);
          }
        };

        switch (props.type) {
          case MenuType.Behaviors:
            return (
              <BehaviorsMenuReact
                key={key}
                ref={setRef}
                keydownProxyEl={keydownProxyEl}
                onSetKeydownProxyEl={setKeydownProxyEl}
                selected={selected}
                metricType={props.metricType}
                open={open}
                eventNames={props.eventNames}
                projectId={props.projectId}
                readonly={props.readonly}
                trackName={props.trackName}
                userId={props.userId}
                workspaceId={props.workspaceId}
                onSelect={handleSelect}
                onCreate={props.onCreate}
                onRender={onChildMenuUpdate}
              />
            );
          case MenuType.Events:
          case MenuType.MultiEvents: {
            const EventsMenuComponent =
              props.type === MenuType.MultiEvents || props.multiple ? MultiEventsMenuReact : EventsMenuReact;
            return (
              <EventsMenuComponent
                key={key}
                ref={setRef}
                isModalOpen={isModalOpen}
                onModalOpen={handleChildMenuOpenedModal}
                onModalClose={handleChildMenuClosedModal}
                keydownProxyEl={keydownProxyEl}
                onSetKeydownProxyEl={setKeydownProxyEl}
                selected={selected}
                tooltipData={tooltipData}
                onTooltipChange={setTooltipData}
                otherOptions={otherOptions}
                inverted={props.inverted}
                includeHidden={props.includeHidden}
                allDataGroups={props.allDataGroups}
                dataGroupId={props.dataGroupId}
                excludeAutotrackEvents={props.excludeAutotrackEvents}
                excludeCustomEvents={props.excludeCustomEvents}
                hideEditControls={props.hideEditControls}
                multiple={props.multiple}
                open={open}
                projectHasPeople={props.projectHasPeople}
                readonly={props.readonly}
                showAllEvents={props.showAllEvents}
                showAnyEvent={props.showAnyEvent}
                showSessionEvent={props.showSessionEvent}
                showSpecialDatasetProperty={props.showSpecialDatasetProperty}
                showTopEvents={props.showTopEvents}
                trackName={props.trackName}
                eventNames={props.eventNames}
                projectId={props.projectId}
                userId={props.userId}
                workspaceId={props.workspaceId}
                onSelect={handleSelect}
                onDelete={handleDelete}
                onRender={onChildMenuUpdate}
              />
            );
          }
          case MenuType.Properties:
            return (
              <PropertiesMenuReact
                key={key}
                ref={setRef}
                isModalOpen={isModalOpen}
                onModalOpen={handleChildMenuOpenedModal}
                onModalClose={handleChildMenuClosedModal}
                listProperty={props.listProperty}
                getSortedMenuSections={props.getSortedMenuSections}
                selected={selected}
                cohortMultiselect={props.cohortMultiselect}
                applyButtonText={props.applyButtonText}
                minSelection={props.minSelection}
                allowBehavioralPropertyCreation={props.allowBehavioralPropertyCreation}
                allowTypecastPillAction={props.allowTypecastPillAction}
                enableAllDataGroupsForScdProps={props.enableAllDataGroupsForScdProps}
                enableWorkspaceFilterRestrictions={props.enableWorkspaceFilterRestrictions}
                disabledItems={props.disabledItems}
                includeCustomProperties={props.includeCustomProperties as any}
                showBorrowedProperties={props.showBorrowedProperties}
                excludeDimensionProperties={props.excludeDimensionProperties}
                excludeLookupTableProperties={props.excludeLookupTableProperties}
                propertyCombineMode={props.propertyCombineMode as PropertyCombineMode}
                specialCohorts={props.specialCohorts}
                otherOptions={otherOptions}
                selectAllMechanism={props.selectAllMechanism}
                showSelectAll={props.showSelectAll}
                selectAllDisabled={props.selectAllDisabled}
                hideRecentlyViewed={props.hideRecentlyViewed}
                projectIsFreePlan={props.projectIsFreePlan}
                selectableSensitiveProperties={props.selectableSensitiveProperties}
                menuItemTypes={props.menuItemTypes}
                contextPanelAlignment={props.contextPanelAlignment}
                typeFilter={props.typeFilter}
                allDataGroups={props.allDataGroups}
                dataGroupId={props.dataGroupId}
                eventNames={props.eventNames}
                hideAllPropertiesSelect={props.hideAllPropertiesSelect}
                hideEditControls={props.hideEditControls}
                hideEvents={props.hideEvents}
                inlineCohortsOverride={props.inlineCohortsOverride}
                multiple={props.multiple}
                open={open}
                projectHasCohorts={props.projectHasCohorts}
                projectHasPeople={props.projectHasPeople}
                projectId={props.projectId}
                readonly={props.readonly}
                showAllEvents={props.showAllEvents}
                showAnyEvent={props.showAnyEvent}
                showSessionEvent={props.showSessionEvent}
                showSpecialDatasetProperty={props.showSpecialDatasetProperty}
                showSpecialDatePropertyUnit={props.showSpecialDatePropertyUnit}
                showTopEvents={props.showTopEvents}
                supportB2bCohorts={props.supportB2bCohorts}
                trackName={props.trackName}
                userId={props.userId}
                workspaceId={props.workspaceId}
                onSelect={(item) => handleSelect({detail: item})}
                onDelete={handleDelete}
                onTooltipContextEditDescription={handleTooltipContextEditDescription}
                onRender={onChildMenuUpdate}
              />
            );
          case MenuType.Filter:
            return (
              <FilterMenuReact
                key={key}
                ref={setRef}
                keydownProxyEl={keydownProxyEl}
                listProperty={props.listProperty}
                selected={selected}
                enableEnhancedListFilters={props.enableEnhancedListFilters}
                enableWorkspaceFilterRestrictions={props.enableWorkspaceFilterRestrictions}
                disableValueSelection={props.disableValueSelection}
                includeCustomProperties={props.includeCustomProperties}
                showBorrowedProperties={props.showBorrowedProperties}
                displayContext={props.displayContext as FilterMenuDisplayContext}
                disabledItems={props.disabledItems}
                otherFilterOptions={props.otherFilterOptions}
                allDataGroups={props.allDataGroups}
                dataGroupId={props.dataGroupId}
                enableAllDataGroupsForScdProps={props.enableAllDataGroupsForScdProps}
                disableAllScdProps={props.disableAllScdProps}
                eventNames={props.eventNames}
                excludeDimensionProperties={props.excludeDimensionProperties}
                excludeLookupTableProperties={props.excludeLookupTableProperties}
                hideEditControls={props.hideEditControls}
                hideEvents={props.hideEvents}
                inlineCohortsOverride={props.inlineCohortsOverride}
                open={open}
                projectHasCohorts={props.projectHasCohorts}
                projectHasPeople={props.projectHasPeople}
                projectId={props.projectId}
                readonly={props.readonly}
                showSpecialDatasetProperty={props.showSpecialDatasetProperty}
                supportB2bCohorts={props.supportB2bCohorts}
                trackName={props.trackName}
                userId={props.userId}
                workspaceId={props.workspaceId}
                onSelect={handleSelect}
                onRender={onChildMenuUpdate}
              />
            );
          case MenuType.Breakdown:
            return (
              <BreakdownMenuReact
                key={key}
                ref={setRef}
                listProperty={props.listProperty}
                dataGroupId={props.dataGroupId}
                open={open}
                projectId={props.projectId}
                selected={props.selected}
                showSpecialDatePropertyUnit={props.showSpecialDatePropertyUnit}
                workspaceId={props.workspaceId}
                includeCustomProperties={props.includeCustomProperties}
                showBorrowedProperties={props.showBorrowedProperties}
                allDataGroups={props.allDataGroups}
                excludeDimensionProperties={props.excludeDimensionProperties}
                excludeLookupTableProperties={props.excludeLookupTableProperties}
                hideAllPropertiesSelect={props.hideAllPropertiesSelect}
                hideEditControls={props.hideEditControls}
                hideEvents={props.hideEvents}
                inlineCohortsOverride={props.inlineCohortsOverride}
                multiple={props.multiple}
                projectHasCohorts={props.projectHasCohorts}
                projectHasPeople={props.projectHasPeople}
                showAllEvents={props.showAllEvents}
                showAnyEvent={props.showAnyEvent}
                showSessionEvent={props.showSessionEvent}
                showSpecialDatasetProperty={props.showSpecialDatasetProperty}
                showTopEvents={props.showTopEvents}
                supportB2bCohorts={props.supportB2bCohorts}
                eventNames={props.eventNames}
                readonly={props.readonly}
                trackName={props.trackName}
                onSelect={handleSelect}
                onRender={onChildMenuUpdate}
              />
            );
          case MenuType.Formulas:
            return (
              <FormulasMenuReact
                key={key}
                ref={setRef}
                selected={selected}
                open={open}
                eventNames={props.eventNames}
                projectId={props.projectId}
                readonly={props.readonly}
                trackName={props.trackName}
                userId={props.userId}
                workspaceId={props.workspaceId}
                keydownProxyEl={keydownProxyEl}
                onSetKeydownProxyEl={setKeydownProxyEl}
                onSelect={handleSelect}
                onCreate={props.onCreate}
                onRender={onChildMenuUpdate}
              />
            );
        }

        return null;
      })()}
    />
  );
});
QueryBlock.displayName = `QueryBlock`;

const defaultProps: QueryBlockParams = {
  advancedMenuSections: [],
  buttonSize: ButtonSize.Medium,
  dataGroups: [],
  disabledItems: {},
  excludedElements: [],
  includeCustomProperties: IncludeCustomPropertyType.None,
  menuAlignment: Directions.Down,
  menuOffset: 0,
  otherAggregationOptions: [],
  otherFilterOptions: [],
  otherGroupByOptions: [],
  selectAllMechanism: SelectAllMechanism.Full,
  selectorType: SelectorType.Text,
  theme: ButtonThemes.Primary,
  typeFilter: [],
};

QueryBlock.defaultProps = defaultProps;

function useChildMenuSync(childMenu: ChildMenuInterface) {
  const {print} = useTranslation();

  const [, forceRender] = useState({});
  const waitingForRender = useRef(false);

  useEffect(() => {
    waitingForRender.current = false;
  });

  const getChildMenuState = () => ({
    selectedLabel: print(childMenu?.selectedLabel?.()),
    getPrefixLabel: print(childMenu?.getPrefixLabel?.()),
    selectedBrowserTooltipLabel: print(childMenu?.selectedBrowserTooltipLabel?.()),
    selectedLeftIcon: childMenu?.selectedLeftIcon?.(),
    selectedRightIcon: childMenu?.selectedRightIcon?.(),
  });

  const lastRenderState = getChildMenuState();

  return () => {
    // since we're depending on child menu state, we need to force a re-render here whenever certain
    // state updates there. this is a hack to help with ui weirdness but the whole relationship needs
    // to be seriously refactored.
    const latestChildMenuState = getChildMenuState();

    if (!isEqual(lastRenderState, latestChildMenuState) && !waitingForRender.current) {
      forceRender({});
      waitingForRender.current = true;
    }
  };
}

export const QueryBlockReact = QueryBlock;
